/**
 * Analysis Mode Schema
 * 
 * Provides structured schema information for the Analysis Mode prompt.
 * The AI uses this to understand which fields can be edited and their possible values.
 */

import {
  cooperationLevels,
  engagementLevels,
  communicationStyles,
  emotionalStates,
  personalityOrientations,
  initialEmotions,
  type CooperationLevel,
  type EngagementLevel,
  type CommunicationStyle,
  type EmotionalState,
  type PersonalityOrientation,
  type InitialEmotion,
  type BehavioralProfile
} from './behavioralDimensions';

/**
 * Schema field definition for Analysis Mode
 */
export interface AnalysisSchemaField {
  path: string;
  type: 'enum' | 'string' | 'array' | 'object';
  description: string;
  values?: Record<string, { label: string; description: string }>;
  example?: string;
}

/**
 * Generate the complete editable schema for Analysis Mode.
 * This is injected into the Analysis Mode prompt so the AI knows what can be changed.
 */
export function getAnalysisSchema(): AnalysisSchemaField[] {
  return [
    {
      path: 'behavioralProfile.cooperation',
      type: 'enum',
      description: 'How willing the character is to help and collaborate',
      values: cooperationLevels
    },
    {
      path: 'behavioralProfile.engagement',
      type: 'enum',
      description: 'How interested and motivated the character is in the interaction',
      values: engagementLevels
    },
    {
      path: 'behavioralProfile.communication',
      type: 'enum',
      description: 'The character\'s communication style and manner of speaking',
      values: communicationStyles
    },
    {
      path: 'behavioralProfile.emotionalState',
      type: 'enum',
      description: 'The character\'s underlying emotional state',
      values: emotionalStates
    },
    {
      path: 'behavioralProfile.orientation',
      type: 'enum',
      description: 'How the character processes information and makes decisions',
      values: personalityOrientations
    },
    {
      path: 'behavioralProfile.initialEmotion',
      type: 'enum',
      description: 'The visual emotion shown by the avatar at the start of conversation',
      values: initialEmotions
    },
    {
      path: 'avatarRules',
      type: 'array',
      description: 'Specific behavioral rules the avatar must follow. Each rule is a string instruction.',
      example: 'Always ask for evidence before agreeing to any proposal'
    },
    {
      path: 'aiHiddenContext',
      type: 'string',
      description: 'Hidden context only the AI sees. Contains secrets, underlying motivations, or information the user must discover through conversation.',
      example: 'The character secretly agrees but needs to save face in front of colleagues'
    },
    {
      path: 'avatarKnowledge',
      type: 'string',
      description: 'Factual knowledge the avatar MUST know and CANNOT invent. Use for product features, company policies, protocols, or any information that must be accurate.',
      example: 'The product costs €99/month. Free trial is 14 days. We integrate with Slack but not Discord.'
    },
    {
      path: 'establishedFacts',
      type: 'string',
      description: 'Ground-truth facts known to BOTH the avatar and the trainee at the start of the session. Use for shared context the conversation builds on (the situation, prior events, agreed-upon background). Distinct from avatarKnowledge (only the avatar knows) and aiHiddenContext (only the avatar knows AND must be discovered).',
      example: 'The team missed last quarter\'s deadline. A new project manager joined two weeks ago. Stakeholders are demanding a status update by Friday.'
    },
    {
      path: 'userReferenceInfo',
      type: 'string',
      description: 'Reference information for the trainee. You CAN edit this field. The content is displayed to the user during the session as reference material (product specs, prices, etc). You can see it here and modify it as needed.',
      example: 'Product pricing: Basic €49/mo, Pro €99/mo, Enterprise €299/mo. Free trial: 30 days.'
    },
    {
      path: 'description',
      type: 'string',
      description: 'The scenario description shown to users and used by the AI to understand context'
    },
    {
      path: 'aiRole',
      type: 'string',
      description: 'The role/job title of the AI character',
      example: 'Senior Project Manager'
    },
    {
      path: 'userRole',
      type: 'string',
      description: 'The role the human user is playing in the scenario',
      example: 'Sales Representative'
    },
    {
      path: 'characterName',
      type: 'string',
      description: 'The name of the AI character (avatar). Adjust this if the technician wants to rename the character.',
      example: 'Charles'
    }
  ];
}

/**
 * Format the schema as a human-readable text block for injection into prompts.
 * Uses plain text suitable for voice communication (no emojis or markdown).
 */
export function formatSchemaForPrompt(): string {
  const schema = getAnalysisSchema();
  const lines: string[] = [
    'EDITABLE FIELDS AND THEIR VALUES:',
    ''
  ];

  for (const field of schema) {
    lines.push(`Field: ${field.path}`);
    lines.push(`  Type: ${field.type}`);
    lines.push(`  Description: ${field.description}`);
    
    if (field.values) {
      lines.push('  Possible values:');
      for (const [key, info] of Object.entries(field.values)) {
        lines.push(`    - "${key}": ${info.description}`);
      }
    }
    
    if (field.example) {
      lines.push(`  Example: ${field.example}`);
    }
    
    lines.push('');
  }

  return lines.join('\n');
}

/**
 * Format current scenario configuration for Analysis Mode prompt.
 */
export function formatCurrentConfig(scenario: {
  behavioralProfile?: Partial<BehavioralProfile>;
  avatarRules?: string[];
  aiHiddenContext?: string;
  avatarKnowledge?: string;
  establishedFacts?: string;
  userReferenceInfo?: string;
  description?: string;
  aiRole?: string;
  userRole?: string;
  characterName?: string;
}): string {
  const lines: string[] = [
    'YOUR CURRENT CONFIGURATION:',
    ''
  ];

  if (scenario.characterName) {
    lines.push(`Character Name: ${scenario.characterName}`);
  }

  if (scenario.aiRole) {
    lines.push(`AI Role: ${scenario.aiRole}`);
  }

  if (scenario.userRole) {
    lines.push(`User Role: ${scenario.userRole}`);
  }

  if (scenario.description) {
    lines.push(`Description: ${scenario.description}`);
  }

  lines.push('');
  lines.push('Behavioral Profile:');
  
  const bp = scenario.behavioralProfile || {};
  // Helper to normalize values (Firestore may store PascalCase, but our keys are lowercase)
  const normalize = (val: string | undefined): string | undefined => val?.toLowerCase();
  
  if (bp.cooperation) {
    const info = cooperationLevels[normalize(bp.cooperation) as CooperationLevel];
    lines.push(`  cooperation: "${bp.cooperation}" (${info?.description || 'unknown'})`);
  }
  if (bp.engagement) {
    const info = engagementLevels[normalize(bp.engagement) as EngagementLevel];
    lines.push(`  engagement: "${bp.engagement}" (${info?.description || 'unknown'})`);
  }
  if (bp.communication) {
    const info = communicationStyles[normalize(bp.communication) as CommunicationStyle];
    lines.push(`  communication: "${bp.communication}" (${info?.description || 'unknown'})`);
  }
  if (bp.emotionalState) {
    const info = emotionalStates[normalize(bp.emotionalState) as EmotionalState];
    lines.push(`  emotionalState: "${bp.emotionalState}" (${info?.description || 'unknown'})`);
  }
  if (bp.orientation) {
    // orientation uses snake_case (detail_oriented, big_picture) - normalize but preserve underscores
    const normalizedOrientation = bp.orientation.toLowerCase().replace(/ /g, '_');
    const info = personalityOrientations[normalizedOrientation as PersonalityOrientation];
    lines.push(`  orientation: "${bp.orientation}" (${info?.description || 'unknown'})`);
  }
  if (bp.initialEmotion) {
    const info = initialEmotions[normalize(bp.initialEmotion) as InitialEmotion];
    lines.push(`  initialEmotion: "${bp.initialEmotion}" (${info?.description || 'unknown'})`);
  }

  if (scenario.avatarRules && scenario.avatarRules.length > 0) {
    lines.push('');
    lines.push('Avatar Rules:');
    scenario.avatarRules.forEach((rule, i) => {
      lines.push(`  ${i}: "${rule}"`);
    });
  }

  if (scenario.aiHiddenContext) {
    lines.push('');
    lines.push(`Hidden Context: ${scenario.aiHiddenContext}`);
  }

  if (scenario.avatarKnowledge) {
    lines.push('');
    lines.push(`Factual Knowledge: ${scenario.avatarKnowledge}`);
  }

  if (scenario.establishedFacts) {
    lines.push('');
    lines.push(`Established Facts (shared with the trainee): ${scenario.establishedFacts}`);
  }

  if (scenario.userReferenceInfo) {
    lines.push('');
    lines.push(`User Reference Info (shown only to trainee): ${scenario.userReferenceInfo}`);
  }

  return lines.join('\n');
}

/**
 * Inference heuristics for the Analysis Mode prompt.
 * Guides the AI to diagnose which configuration fields affect observed behavior.
 */
export function getInferenceHeuristics(): string {
  return `INFERENCE GUIDELINES - How to diagnose configuration issues:

Your job is to figure out which configuration fields are causing the behavior the technician describes. Each field controls a distinct aspect of the avatar's behavior. Your task is to map the technician's observation to the correct field.

The behavioral profile fields and what they control:

- cooperation: How willing the character is to help, collaborate, or compromise. This field is about the character's stance toward requests and proposals — whether they resist, meet halfway, or readily agree. If the technician describes the avatar as unreasonably stubborn or unreasonably accommodating, this is the field to adjust.

- engagement: How interested and motivated the character is in the interaction itself. This field controls whether the character seems invested in the conversation or distant and detached. If the technician describes the avatar as distracted, disinterested, or conversely overly eager and intense, this is the field to adjust.

- communication: How the character expresses themselves — the length, tone, and directness of their responses. This field is specifically about the speaking style, not about what the character says or feels. Only adjust this if the issue is clearly about how the avatar speaks rather than what they do or believe. Do not default to this field for every issue.

- emotionalState: The underlying emotional tone that drives the character's reactions. This field controls the emotional color of responses — whether the character seems anxious, confident, suspicious, calm, or stressed. Adjust this when the technician describes the avatar as emotionally off in some way.

- orientation: How the character processes information and makes decisions. This field controls whether the character focuses on concrete details, thinks in broad patterns, follows logic, or follows feelings. Adjust this when the technician describes the character's thinking style as mismatched.

The content fields and what they control:

- avatarRules: Specific behavioral constraints that override general tendencies. These are hard instructions that can cause rigid or unexpected behavior. Check this field when the avatar refuses to do something or behaves in a way that seems artificially blocked.

- aiHiddenContext: Secret motivations, background context, and information the character knows but the user does not. Adjust this when the avatar seems to lack understanding of their own situation or behaves in ways that don't align with their hidden role.

- avatarKnowledge: Factual information the character must know and cannot invent. Adjust this when the avatar gives incorrect facts or seems unaware of information they should possess.

- establishedFacts: Ground-truth shared context that BOTH the character and the trainee already know going into the conversation. Adjust this when the avatar seems unaware of the situation's premise, or when the conversation needs a different shared starting point. Distinct from avatarKnowledge (private to the avatar) and aiHiddenContext (private AND meant to be hidden from the trainee).

How to diagnose:

First, listen to what the technician actually observed. Identify the specific aspect of behavior that is wrong — is it about willingness to help, interest in the conversation, speaking style, emotional tone, thinking style, rigid rules, missing context, or wrong facts?

Then map that aspect to the corresponding field above. Explain your reasoning briefly before suggesting any change. Consider all fields equally. Do not let communication dominate your diagnosis just because it is easy to attribute speech patterns to it. Most behavior issues are about cooperation, engagement, emotional state, or rules — not about communication style.

When multiple fields could be involved, suggest the change that addresses the root cause rather than the surface symptom.`;
}
